import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { productConfig } from '../config/product';
import '../features/auth/login.css';

type Status = 'verifying' | 'error';

export function AuthVerifyPage() {
  const { verifyMagicLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState<string | null>(null);
  // Track the token we've already attempted so StrictMode's double-invoke can't
  // burn a single-use token twice, while still allowing a genuinely new token.
  const attemptedToken = useRef<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (attemptedToken.current === token) return;
    attemptedToken.current = token;

    if (!token) {
      setError('This sign-in link is missing its token.');
      setStatus('error');
      return;
    }

    void (async () => {
      try {
        await verifyMagicLink(token);
        void navigate('/console', { replace: true });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'This sign-in link is invalid or has expired.',
        );
        setStatus('error');
      }
    })();
  }, [params, verifyMagicLink, navigate]);

  return (
    <main className="stage bg-grid login-stage" data-screen-label="Verifying sign-in">
      <div className="glow bg-glow g1" aria-hidden="true" />
      <div className="glow bg-glow g2" aria-hidden="true" />

      <div className="wrap">
        <section className="signin-card" aria-labelledby="verify-heading">
          <div className="brand" aria-label={productConfig.name}>
            <span className="nm">
              RegenLoop<span className="ai">.ai</span>
            </span>
          </div>

          {status === 'verifying' ? (
            <>
              <h1 id="verify-heading">Signing you in…</h1>
              <p className="sub">Verifying your sign-in link.</p>
            </>
          ) : (
            <>
              <h1 id="verify-heading">Sign-in link problem</h1>
              <p className="sub" role="alert">
                {error}
              </p>
              <button type="button" className="submit" onClick={() => void navigate('/login')}>
                <span className="lbl">Request a new link</span>
              </button>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
